import {useEffect, useState, type ReactNode} from 'react';

import {useRouter} from 'next/router';

import {AppDto} from 'types/Dto';

import {fetchApp} from 'api/apps/apps';

import NotFound from 'pages/404';

import useApps from 'hooks/useApps';
import useUserHasRoles from 'hooks/user/useUserHasRoles';

import Loading from 'components/Common/Loading';

import Onboarding from '../Onboarding';

type AppLoaderProps = {
  children: (app?: AppDto) => ReactNode;
  disallowRedirect?: boolean;
};

const AppLoader = ({children, disallowRedirect}: AppLoaderProps) => {
  const {
    apps,
    loading,
    hasAccess,
    getLastSelected,
    selectedApp,
    setSelectedApp,
  } = useApps();
  const [loaded, setLoaded] = useState(false);
  const hasAdminAccess = useUserHasRoles('admin', 'staff');

  const router = useRouter();

  useEffect(() => {
    if (loading) return;

    // check if has access
    if (selectedApp && hasAccess(selectedApp)) {
      setLoaded(true);
      return;
    }

    // check no apps
    if (!apps.length) {
      setSelectedApp(null);
      setLoaded(true);
      return;
    }

    // check las selected
    const lastSelected = getLastSelected();

    const appIdUrl = router.query.app_id as string;
    if (hasAdminAccess && appIdUrl) {
      fetchApp(appIdUrl)
        .then(app => {
          if (app) {
            setSelectedApp(app);
          }
        })
        .finally(() => {
          setLoaded(true);
        });
    } else {
      const appIdToSelect = appIdUrl ?? lastSelected;

      const newAppSelected = apps.find(e => e.id === appIdToSelect) ?? apps[0];

      setSelectedApp(newAppSelected);
      setLoaded(true);
    }
  }, [
    setSelectedApp,
    getLastSelected,
    hasAccess,
    selectedApp,
    apps,
    loading,
    router.query.app_id,
    hasAdminAccess,
  ]);

  if (!loaded && !selectedApp) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (!disallowRedirect && !selectedApp) {
    if (apps.length === 0) {
      return <Onboarding />;
    }
    return <NotFound />;
  }

  return (
    <AppRender key={selectedApp?.id ?? 'app'}>
      {children(selectedApp ?? undefined)}
    </AppRender>
  );
};

type AppRenderProps = {
  children: ReactNode;
};

const AppRender = ({children}: AppRenderProps) => {
  return <>{children}</>;
};

export default AppLoader;
